<script setup lang="ts">
import { WebSocketEvent } from '@/types/constantsEnum'
import { useWebSocketStore } from '@/store/modules/websocket'
import { WebSocketNotification } from '@/utils/notification'
import type { WsResponse } from '@/utils/websocket'
import { useRouter } from 'vue-router'
import LittleBtn from '@/components/MusicControls/LittleBtn.vue'
import MusicControls from '@/components/MusicControls/MusicControls.vue'
import { useMusicPlayerStoreHook } from '@/store/modules/musicPlayer'
import { useMusicTagsStoreHook } from '@/store/modules/musicTags'
import { initlang } from '@/config/initLang'
import { Lang } from '@/types/constantsEnum'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import zhTw from 'element-plus/dist/locale/zh-tw.mjs'
import en from 'element-plus/dist/locale/en.mjs'
import DownloadProgress from '@/components/Download/DownloadProgress.vue'
import { useDownloadProgressStoreHook } from '@/store/modules/downloadProgress'
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig'
import { emitter } from '@/utils/mitt'
const router = useRouter()
const transitionName = ref('fade')
const wsStore = useWebSocketStore()
const musicPlayerStore = useMusicPlayerStoreHook()
const musicTagsStore = useMusicTagsStoreHook()
const downloadProgressStore = useDownloadProgressStoreHook()

const handleMessage = (data: WsResponse) => {
  // 根据消息类型显示不同的通知
  WebSocketNotification.notify(data.event, data)
}
// 监听路由变化
watch(
  () => router.currentRoute.value,
  (to, from) => {
    const toDepth = to.meta.level || 0
    const fromDepth = from.meta.level || 0
    transitionName.value = toDepth > fromDepth ? 'slide-left' : 'slide-right'
  },
)
watch(
  () => musicPlayerStore.currentSongIndex,
  (newVal) => {
    emitter.emit('emitMusicCurrentPlay', {
      index: newVal,
      originType: musicPlayerStore.originType,
    })
  },
)

// 动画钩子示例
const beforeEnter = (el) => {
  el.style.willChange = 'transform, opacity'
}

const afterEnter = (el) => {
  el.style.willChange = 'auto'
}

const currentLocale = computed(() => {
  const lang = initlang()
  switch (lang) {
    case Lang.LAN_CN:
      return zhCn
    case Lang.LAN_EN:
      return en
    case Lang.LAN_TW:
      return zhTw
    default:
      return zhCn
  }
})

// 处理关闭下载进度
const handleCloseDownloadProgress = () => {
  downloadProgressStore.closeProgress()
}

onMounted(async () => {
  await useSystemConfigStoreHook().getSystemConfig()
  // 只需要监听用户事件即可，消息类型在数据中
  wsStore.addMessageListener(WebSocketEvent.UserEvent, handleMessage)

  // 预加载音乐标签数据
  musicTagsStore.fetchMusicTags().catch((error) => {
    console.error('预加载标签数据失败:', error)
  })
})

onUnmounted(() => {
  wsStore.removeMessageListener(WebSocketEvent.UserEvent, handleMessage)
})
</script>
<template>
  <div class="app-container">
    <el-config-provider :locale="currentLocale">
      <router-view v-slot="{ Component, route }">
        <transition
          :name="transitionName"
          mode="out-in"
          @before-enter="beforeEnter"
          @after-enter="afterEnter"
        >
          <div class="min-w-[100vw] min-h-[100vh]">
            <component :is="Component" :key="route.meta.usePathKey ? route.path : undefined" />
            <LittleBtn />
            <MusicControls />
          </div>
        </transition>
      </router-view>
    </el-config-provider>

    <!-- 全局下载进度组件 -->
    <DownloadProgress
      :progress="downloadProgressStore.progress"
      :visible="downloadProgressStore.visible"
      :title="downloadProgressStore.title"
      @close="handleCloseDownloadProgress"
    />
  </div>
</template>

<style>
:root {
  /* 只保留 menu 相关的变量，并保持命名一致 */
  --menu-menub-bg: linear-gradient(90deg, #47cdf3 0%, #0770ed 39%, #0770ed 100%);
  --menu-menu-text: rgba(255, 255, 255, 0.5);
  --home-aisound-btn: linear-gradient(73deg, #0770ed 0%, #0770ed 0%, #47cdf3 103%);
  --menu-sub-menu-active-text: #fff;
  --theme-text-color: #000000;
  --safe-area-width: 1440px;
  --theme-color: #0770ed;
  --btn-hover: #00c6fc;
  --theme-color2: #47cdf3;
  --login-input-height: 45px;
  --login-input-max-width: 384px;
  --text-size-12: 12px;
  --text-size-14: 14px;
  --text-size-default: 16px;
  --text-size-18: 18px;
  --text-size-20: 20px;
  --text-size-22: 22px;
  --text-size-24: 24px;
  --text-size-26: 26px;
  --text-size-28: 28px;
  --text-size-30: 30px;
  --text-size-48: 48px;
}

/* 基础动画 */
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

/* 滑动动画 */
.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-30%);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* 公共动画样式 */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.4s cubic-bezier(0.33, 1, 0.68, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  backface-visibility: hidden;
  perspective: 1000px;
}

.app-container {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
}

/* 优化处理 */
.router-view-wrapper {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
}
</style>
